import type { BrokerSymbol, CategoryTranslationMap, KLinePoint, SymbolCategory } from '~/api/wikitrade-market'

import { DEFAULT_SYMBOL } from './constants'

// 接口返回里价格可能是字符串、数字或空值；这里只接受可预期的基础类型参与数值格式化。
const toNumericText = (value: unknown) => {
  if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
    return String(value)
  }

  return ''
}

const parseNumber = (value: unknown) => {
  const parsed = typeof value === 'number' ? value : Number(toNumericText(value).replace(/,/g, ''))
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

// 当品种配置没有给 price_precision 时，根据最后一个价格兜底推断小数位。
// 限制在 2-6 位之间，避免异常字符串把坐标轴和价格线撑得过长。
const inferPrecision = (value: unknown) => {
  const text = toNumericText(value)
  const decimalLength = text.includes('.') ? (text.split('.')[1]?.length ?? 2) : 2
  return Math.min(Math.max(decimalLength, 2), 6)
}

// 品种展示优先使用币种名称，缺少结算币时退化成单边名称或原始 symbol。
export const formatSymbolName = (symbol?: BrokerSymbol) => {
  if (!symbol) return DEFAULT_SYMBOL
  const base = symbol.base_currency_name || symbol.base_currency || symbol.symbol
  const settlement = symbol.settlement_currency_name || symbol.settlement_currency
  return settlement ? `${base}/${settlement}` : base
}

// 分类名称优先使用翻译接口结果；翻译缺失时按中文、通用名称、英文、code 逐级兜底。
export const getCategoryName = (category: SymbolCategory, translations?: CategoryTranslationMap | null) => {
  const translated = translations?.[category.code]
  if (translated) return translated
  return category.zh_name || category.name || category.en_name || category.code
}

// 不同聚合路由返回的分类字段名不完全一致，统一在这里做兼容，页面只关心最终 code。
export const getSymbolCategoryCode = (symbol: BrokerSymbol) => {
  const record = symbol as Record<string, unknown>
  const keys = [
    'symbol_code',
    'symbolCode',
    'symbol_category',
    'symbol_category_code',
    'symbolCategory',
    'symbolCategoryCode',
    'category_code',
    'categoryCode',
    'category',
    'group',
    'group_code',
    'groupCode',
    'symbol_type',
    'symbolType',
    'type',
    'instrument_type',
    'instrumentType',
  ]

  for (const key of keys) {
    const value = record[key]
    if (typeof value === 'string' || typeof value === 'number') {
      const categoryCode = String(value).trim()
      if (categoryCode) return categoryCode
    }
  }

  return ''
}

// K 线组件的价格格式化、Y 轴精度和价格线精度都走同一个方法，避免展示口径不一致。
export const getPrecision = (symbol?: BrokerSymbol, fallbackValue?: unknown) => {
  return Number.isFinite(symbol?.price_precision) ? Number(symbol?.price_precision) : inferPrecision(fallbackValue)
}

// Element、K 线 tooltip 和价格轴共享这个格式化出口，异常值统一显示占位符。
export const formatPrice = (value: unknown, precision = 2) => {
  const number = parseNumber(value)
  return Number.isFinite(number) ? number.toFixed(precision) : '--'
}

// 实时 K 线优先覆盖同一根蜡烛，因为同一个周期内 WebSocket 会持续推送最新收盘价。
// 如果推来的是新周期蜡烛，则追加到尾部；乱序时再排序，保证组件拿到递增时间序列。
export const mergeRealtimePoint = (current: KLinePoint[], incoming: KLinePoint) => {
  const matchedIndex = current.findIndex((point) => point.time === incoming.time)
  if (matchedIndex >= 0) {
    return current.map((point, index) => (index === matchedIndex ? incoming : point))
  }

  if (!current.length || incoming.time > current[current.length - 1]!.time) {
    return [...current, incoming]
  }

  return [...current, incoming].toSorted((left, right) => left.time - right.time)
}
